import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trainingApi } from '../../lib/api';
import { Badge } from '../ui/badge';
import { BookOpen, CheckCircle2, ClipboardCheck, GraduationCap, Loader2, Lock, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import EvaluationDetail from './EvaluationDetail';
import RegisterDetail from './RegisterDetail';

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('it-IT') : '—';

export default function StaffTrainingPanel({ staffId }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [openEval, setOpenEval] = useState(null);
  const [openReg, setOpenReg] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const openChapter = async (c) => {
    if (!c.unlocked || openingId) return;
    setOpeningId(c.file_id);
    const win = window.open('', '_blank');
    try {
      const r = await trainingApi.downloadFile(c.file_id);
      const url = URL.createObjectURL(new Blob([r.data], { type: c.content_type || 'application/pdf' }));
      if (win && !win.closed) win.location = url; else window.open(url, '_blank');
    } catch {
      if (win && !win.closed) win.close();
      toast.error('Errore apertura capitolo');
    } finally {
      setOpeningId(null);
    }
  };

  const load = useCallback(() => {
    trainingApi.staffSummary(staffId)
      .then(r => setData(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento percorso formativo'); setData({ chapters: [], unlocked_count: 0, registers: [], evaluations: [] }); });
  }, [staffId]);

  useEffect(() => {
    load();
    trainingApi.meta().then(r => setMeta(r.data)).catch(() => setMeta(null));
  }, [load]);

  if (!data) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const lastEval = data.evaluations[0] || null;
  const activeReg = data.registers.find(r => r.status === 'in_corso') || data.registers[0] || null;
  const totHours = data.registers.reduce((a, r) => a + (r.hours || 0), 0);
  const pct = data.chapters.length ? Math.round((data.unlocked_count / data.chapters.length) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="staff-training-panel">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-slate-900 text-white p-4" data-testid="training-level-card">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Livello raggiunto
          </p>
          <p className="text-xl font-bold mt-1">{lastEval?.level || '—'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {lastEval ? `Ultima valutazione: ${lastEval.total ?? 0}/${lastEval.max_total ?? '—'} · ${fmtDate(lastEval.date)}` : 'Nessuna valutazione'}
          </p>
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="training-manual-card">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Manuale
          </p>
          <p className="text-xl font-bold text-slate-900 mt-1 tabular-nums">
            {data.unlocked_count}<span className="text-sm text-slate-400"> / {data.chapters.length} capitoli</span>
          </p>
          <div className="h-1.5 bg-slate-100 mt-2">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="training-hours-card">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <NotebookPen className="w-3.5 h-3.5" /> Formazione erogata
          </p>
          <p className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{totHours || 0}<span className="text-sm text-slate-400"> ore</span></p>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeReg
              ? `Registro ${activeReg.status === 'completato' ? 'completato' : 'in corso'}${activeReg.next_check_date ? ` · verifica ${fmtDate(activeReg.next_check_date)}` : ''}`
              : 'Nessun registro formativo'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Capitoli */}
        <div className="bg-white border border-slate-200 p-4" data-testid="training-chapters-list">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Capitoli del manuale</h3>
            <Link to="/training?tab=manual" className="text-xs underline text-slate-500 hover:text-slate-900" data-testid="training-manual-link">
              Gestisci
            </Link>
          </div>
          {data.chapters.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Nessun capitolo nel manuale</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.chapters.map(c => (
                <div key={c.file_id}
                     className={`flex items-center gap-2 py-1.5 ${c.unlocked ? 'cursor-pointer hover:bg-slate-50 -mx-2 px-2 transition-colors' : ''}`}
                     onClick={() => openChapter(c)}
                     title={c.unlocked ? 'Apri capitolo' : undefined}
                     data-testid={`training-chapter-${c.file_id}`}>
                  {c.unlocked
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    : <Lock className="w-4 h-4 text-slate-300 shrink-0" />}
                  <span className={`text-sm flex-1 ${c.unlocked ? 'text-slate-800' : 'text-slate-400'}`}>
                    {c.chapter_no != null && <span className="tabular-nums text-slate-400 mr-1.5">{String(c.chapter_no).padStart(2, '0')}</span>}
                    {c.title}
                  </span>
                  {c.unlocked && c.unlocked_at && (
                    <span className="text-[10px] text-slate-400">{fmtDate(c.unlocked_at)}</span>
                  )}
                  {c.unlocked && (openingId === c.file_id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" />
                    : <BookOpen className="w-3.5 h-3.5 text-slate-300 shrink-0" />)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Valutazioni */}
          <div className="bg-white border border-slate-200 p-4" data-testid="training-evaluations-list">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Valutazioni</h3>
              <Link to="/training?tab=evaluations" className="text-xs underline text-slate-500 hover:text-slate-900" data-testid="training-evaluations-link">
                Apri modulo
              </Link>
            </div>
            {data.evaluations.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center flex items-center justify-center gap-2">
                <ClipboardCheck className="w-4 h-4" /> Nessuna scheda di valutazione
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.evaluations.map(ev => (
                  <div key={ev.evaluation_id}
                       className="flex items-center gap-2 py-2 flex-wrap cursor-pointer hover:bg-slate-50 -mx-2 px-2 transition-colors"
                       onClick={() => meta && setOpenEval(ev.evaluation_id)}
                       data-testid={`training-eval-${ev.evaluation_id}`}>
                    <span className="text-sm text-slate-700">{fmtDate(ev.date)}</span>
                    <span className="text-sm tabular-nums text-slate-900 font-medium">{ev.total ?? 0}<span className="text-slate-400 font-normal">/{ev.max_total ?? '—'}</span></span>
                    {ev.level && <Badge variant="outline" className="bg-slate-100 text-[10px]">{ev.level}</Badge>}
                    <Badge variant="outline" className={`ml-auto text-[10px] ${ev.status === 'final'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {ev.status === 'final' ? 'Definitiva' : 'Bozza'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Registri */}
          <div className="bg-white border border-slate-200 p-4" data-testid="training-registers-list">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Registri formativi</h3>
              <Link to="/training?tab=registers" className="text-xs underline text-slate-500 hover:text-slate-900" data-testid="training-registers-link">
                Apri modulo
              </Link>
            </div>
            {data.registers.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center flex items-center justify-center gap-2">
                <NotebookPen className="w-4 h-4" /> Nessun registro formativo
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.registers.map(r => (
                  <div key={r.register_id}
                       className="flex items-center gap-2 py-2 flex-wrap cursor-pointer hover:bg-slate-50 -mx-2 px-2 transition-colors"
                       onClick={() => meta && setOpenReg(r.register_id)}
                       data-testid={`training-register-${r.register_id}`}>
                    <span className="text-sm text-slate-700">{fmtDate(r.start_date)}</span>
                    <span className="text-xs text-slate-500">{r.tutor_name ? `Tutor: ${r.tutor_name}` : ''}</span>
                    <span className="text-xs text-slate-500 tabular-nums">{r.hours || 0}h · {r.modules_count} moduli</span>
                    <Badge variant="outline" className={`ml-auto text-[10px] ${r.status === 'completato'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {r.status === 'completato' ? 'Completato' : 'In corso'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {openEval && meta && (
        <EvaluationDetail evaluationId={openEval} meta={meta}
                          onClose={() => { setOpenEval(null); load(); }} />
      )}
      {openReg && meta && (
        <RegisterDetail registerId={openReg} meta={meta}
                        onClose={() => { setOpenReg(null); load(); }} />
      )}
    </div>
  );
}
